/** @jsxImportSource @emotion/react */
import { Modal } from "../../../components/Modal";
import * as yup from "yup";
import { MenuOverwriteSchedule, WeekDay } from "../models/Menus";
import {
  FormProvider,
  useFieldArray,
  UseFieldArrayAppend,
  useForm,
} from "react-hook-form";
import { css } from "@emotion/react";
import { CheckboxField } from "../../../components/forms/fields/CheckboxField";
import { OverwriteMenuModalFormValues } from "./OverwriteMenuModal";
import { FormRow } from "../../common/hook-form/FormRow";
import { TimePickerInput } from "../../../components/forms/inputs/TimePickerInput";
import { yupResolver } from "@hookform/resolvers/yup";
import { ButtonAction, ButtonPrimary } from "../../../components/NewButtons";
import { StyledForm } from "../../../components/StyledForm";
import { ErrorMessage } from "@hookform/error-message";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { SwitchInput } from "../../../components/forms/inputs/SwitchInput";
import moment from "moment";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  append: UseFieldArrayAppend<OverwriteMenuModalFormValues, "schedules">;
};

const modalStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const daysOfWeekWrapperStyle = css`
  display: block;
`;

const dayOfWeekStyle = css`
  display: inline-block;
  margin-bottom: 4px;
  margin-top: 4px;
  margin-right: 16px;
`;

const buttonsWrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
`;

const schema = yup.object().shape({
  daysOfWeek: yup
    .array()
    .of(yup.object().shape({ value: yup.number() }))
    .min(1, "Add days of the week to add achedule"),
  endTime: yup.string().when("isAllDay", {
    is: true,
    then: yup.string().optional(),
    otherwise: yup
      .string()
      .required("Add end time to continue")
      .test(
        "is-valid-time",
        "End time must be after start time",
        (value, ctx) => {
          if (!value) {
            return false;
          }
          const [hours, minutes] = value.split(":").map(Number);
          const endTime = moment().hour(hours).minute(minutes);
          const startTimeValue = ctx.parent.startTime;
          if (!startTimeValue) {
            return false;
          }
          const [startHours, startMinutes] = startTimeValue
            .split(":")
            .map(Number);
          const startTime = moment().hour(startHours).minute(startMinutes);
          return endTime.isAfter(startTime);
        }
      ),
  }),
  startTime: yup.string().when("isAllDay", {
    is: true,
    then: yup.string().optional(),
    otherwise: yup.string().required("Add start time to continue"),
  }),
  isAllDay: yup.boolean(),
});

type FormValues = Omit<MenuOverwriteSchedule, "dayOfWeek"> & {
  daysOfWeek: { value: WeekDay }[];
  isAllDay: boolean;
};

export const AddScheduleModal = ({ isOpen, onClose, append }: Props) => {
  const formMethods = useForm<FormValues>({
    defaultValues: {
      endTime: "",
      startTime: "",
      daysOfWeek: [],
      isAllDay: false,
    },
    resolver: yupResolver(schema),
  });

  const { fields, append: appendDayOfWeek, remove } = useFieldArray({
    control: formMethods.control,
    name: "daysOfWeek",
  });

  const handleClose = () => {
    formMethods.reset();
    onClose();
  };

  const onSubmit = (data: FormValues) => {
    data.daysOfWeek.forEach((day) => {
      append({
        dayOfWeek: day.value,
        startTime: data.isAllDay ? "00:00:00" : data.startTime,
        endTime: data.isAllDay ? "00:00:00" : data.endTime,
      });
    });
    handleClose();
  };

  return (
    <FormProvider {...formMethods}>
      <StyledForm>
        <Modal
          isOpen={isOpen}
          onClose={handleClose}
          header="New availability schedule"
          subtitle="Configure the availability for this menu schedule."
        >
          <div css={modalStyle}>
            <div css={daysOfWeekWrapperStyle}>
              {Object.keys(WeekDay)
                .filter((day) => isNaN(Number(day)))
                .map((day, i) => (
                  <div key={i} css={dayOfWeekStyle}>
                    <CheckboxField
                      label={day}
                      checked={fields.some(
                        (field) =>
                          field.value === WeekDay[day as keyof typeof WeekDay]
                      )}
                      onChange={(e) => {
                        if (e.target.checked) {
                          appendDayOfWeek({
                            value: WeekDay[day as keyof typeof WeekDay],
                          });
                        } else {
                          const index = fields.findIndex(
                            (field) =>
                              field.value ===
                              WeekDay[day as keyof typeof WeekDay]
                          );
                          remove(index);
                        }
                      }}
                    />
                  </div>
                ))}
            </div>
            <ErrorMessage
              errors={formMethods.formState.errors}
              name="daysOfWeek"
              render={({ message }) => (
                <StyledText
                  color={colors.errorDark}
                  className="bodyTextRegular mb-0"
                >
                  {message}
                </StyledText>
              )}
            />
            <FormRow>
              <TimePickerInput
                label="Start time"
                name="startTime"
                // Allowing users to select seconds
                step="1"
                disabled={formMethods.watch("isAllDay")}
              />
              <TimePickerInput
                label="End time"
                name="endTime"
                disabled={formMethods.watch("isAllDay")}
                // Allowing users to select seconds
                step="1"
              />
            </FormRow>
            <SwitchInput
              label="Should apply all day"
              name="isAllDay"
              onChange={() => {
                formMethods.trigger("startTime");
                formMethods.trigger("endTime");
                formMethods.setValue("startTime", "");
                formMethods.setValue("endTime", "");
              }}
            />
            <div css={buttonsWrapperStyle}>
              <ButtonPrimary onClick={handleClose}>Cancel</ButtonPrimary>
              <ButtonAction onClick={formMethods.handleSubmit(onSubmit)}>
                Add schedule
              </ButtonAction>
            </div>
          </div>
        </Modal>
      </StyledForm>
    </FormProvider>
  );
};
